import React, {useCallback, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import {
  Alert,
  Box,
  Button,
  Skeleton,
  FormControl,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import {AxiosResponse} from 'axios';
import {useStylesed} from '../../../../../components/styles/commonStyled';
import {
  IAdditionalPlayerInformationType,
  IMessage1,
  MessagesProps,
} from '../../../../../dto/Message.dto';
import RetryPage from '../../../../../components/styles/RetryButton';
import {IErrorResponse} from '../../../../../dto/Html.dto';
import {apiPost} from '../../../../../axiosConfig';
import {IApiSuccessMsgResponse} from '../../../../../dto/API.dto';
import DialogApiRequestStatus from '../../../../../components/dialogs/DialogApiRequestStatus';
import TrackNameCell from '../../../../../components/Button/TrackNameCell';
import {SponsorRequestDetailsCard} from '../../../../sponsor/SponsorshipRequest';
import {UserCanAny} from '../../../../../components/UserCan';
import {useTaskContext} from '../../../../task/TaskContext';
import {useRqVoiceArtists} from '../../../../../react_query/users/CompanyContacts';
import {IVoiceAssignment} from '../../../../../dto/VoiceArtistAssignment';
import MusicPreviewBtn from '../../../../../components/music/MusicPreviewBtn';
import {broadCastCloseTab} from '../../../../../react_query/tasks/ListTasks';
interface IMsgAudioUpload extends IMessage1 {
  prepopulated_file_name: string;
  matched: boolean;
}
const OnboardRecording = () => {
  const taskParam = useParams();
  const {isPreview, isNR} = useTaskContext();
  const taskId = Number(taskParam.task_id);
  const classes = useStylesed();
  const [selectedFile, setSelectedFile] = React.useState<File[]>([]);
  const [content, setContent] = React.useState<IMsgAudioUpload[]>([]);
  const [filesSelected, setFilesSelected] = React.useState(0);
  const [, setRetryTime] = React.useState(Date.now());
  const [voices, setVoices] = React.useState<IVoiceAssignment[]>([]);
  const [voiceType, setVoiceType] = useState('');
  const [isMessagesLoaded, setIsMessagesLoaded] = useState(false);

  const [apiState, setApiState] = useState({
    status: '',
    error: null,
    successMsg: '',
  });
  const {data, isError, isFetching, error, isFetched} = useTaskContext();
  const isWalkup = useMemo(() => {
    if (data && isFetched && data.requestable_type === 'walkup-request') {
      return true;
    }
    return false;
  }, [data, isFetched]);
  const isTeamRoster = useMemo(() => {
    if (data && isFetched && data.requestable_type === 'team-roster') {
      return true;
    }
    return false;
  }, [data, isFetched]);
  const {data: voiceArtistData} = useRqVoiceArtists(
    Number(data?.company?.id),
    {
      type: voiceType,
      is_assigned_voices: 1,
      voices: voices
        ? voices.map(o => o.voice_artist).filter(Boolean)
        : undefined,
    },
    Boolean(data?.company?.id) && Boolean(voiceType) && isMessagesLoaded,
  );

  const handleSubmit = React.useCallback(() => {
    setApiState({status: 'pending', error: null, successMsg: ''});
    const payload = new FormData();
    selectedFile.forEach((file, index) => {
      payload.append(`files[${index}]`, file);
    });
    voices.forEach((values, index) => {
      payload.append(`voice[${index}][message_id]`, String(values.message_id));
      payload.append(`voice[${index}][voice_id]`, String(values.voice_artist));
    });
    payload.append('task_id', String(taskId));
    apiPost(`v1/task/${taskId}/upload/recording`, payload)
      .then((response: AxiosResponse<IApiSuccessMsgResponse>) => {
        setApiState({
          status: 'success',
          error: null,
          successMsg: response.data.message,
        });
        broadCastCloseTab();
      })
      .catch(error => {
        setApiState({
          status: 'error',
          error,
          successMsg: '',
        });
      });
  }, [selectedFile, taskId, voices]);
  const handleEdit = useCallback(() => {
    setApiState({status: '', error: null, successMsg: ''});
  }, []);
  const handleFileChange = React.useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      setSelectedFile(prev => {
        const files = Array.from(e.target.files || []);
        const existingFileNames = new Set(
          prev.map(file => file.name.split('.')[0]),
        );
        const filteredFiles = files.filter(file => {
          const fileName = file.name.split('.')[0];
          return !existingFileNames.has(fileName);
        });
        return [...prev, ...filteredFiles];
      });
    },
    [],
  );

  React.useEffect(() => {
    if (data && !isFetching && !isError) {
      let fileNames: string[] = [];
      selectedFile.forEach(file => {
        const fileName = file.name.split('.');
        fileNames.push(fileName[0] as string);
      });
      if (data?.requestable?.messages) {
        const contents = data.requestable.messages
          .filter(o => {
            if (isWalkup || isTeamRoster) {
              return true;
            }
            return o.message !== '';
          })
          .map(val => {
            const msgAudioUpload = {...val, matched: false} as IMsgAudioUpload;
            msgAudioUpload.matched = fileNames.includes(
              msgAudioUpload.prepopulated_file_name,
            );
            return msgAudioUpload;
          });

        const length = contents.filter(
          (v: IMsgAudioUpload) => !v.matched,
        ).length;
        setContent(contents);
        setFilesSelected(length);
        if (!isMessagesLoaded) {
          if (data.task_slug === 'base-recording-pending') {
            setVoiceType('djvp');
          } else if (data.task_slug === 'stinger-recording-pending') {
            setVoiceType('svp');
          } else {
            setVoiceType('lmvp');
          }

          if (isNR && data.task_slug === 'base-recording-pending') {
            setVoices(
              contents.map(val => {
                return {
                  message_id: val.id,
                  voice_artist: val.voice_artist,
                };
              }),
            );
          }
        }
      }
      setIsMessagesLoaded(true);
    }
  }, [
    data,
    selectedFile,
    isFetched,
    isError,
    isNR,
    isWalkup,
    isTeamRoster,
    isFetching,
    isMessagesLoaded,
  ]);

  if (isError) {
    const errResp = error as IErrorResponse;
    return (
      <RetryPage
        pageTitle='List messages'
        errResp={errResp?.response}
        setRetryTime={setRetryTime}
        queryKey={[`v1/task/${taskId}/recording`]}
      />
    );
  }
  const handleClose = () => {
    setApiState({
      status: '',
      error: null,
      successMsg: '',
    });
    window.close();
  };

  const handleChangeVoiceArtist = (
    event: SelectChangeEvent<string>,
    messageId: number,
  ) => {
    const newVoiceArtist = parseInt(event.target.value, 10);
    setVoices(prev => {
      const existingIndex = prev.findIndex(
        voice => voice.message_id === messageId,
      );
      if (existingIndex !== -1) {
        return prev.map((voice, index) =>
          index === existingIndex
            ? {...voice, voice_artist: newVoiceArtist}
            : voice,
        );
      } else {
        return [...prev, {message_id: messageId, voice_artist: newVoiceArtist}];
      }
    });
  };
  return (
    <Box className='p-2'>
      {data?.requestable_id &&
      data.requestable_type === 'sponsor-commercial' ? (
        <UserCanAny scope='is-admin,sponsor:view,partner'>
          <SponsorRequestDetailsCard
            sponsorRequestId={data?.requestable_id}
            type='recording'
          />
        </UserCanAny>
      ) : null}
      {data &&
      data.requestable_type === 'sponsor-commercial' &&
      data.requestable &&
      data.requestable.message ? (
        <SponsorMessageDetailsCmpt message={data?.requestable.message} />
      ) : null}
      <Alert severity='error'>File name must be as given in table</Alert>
      {data && !isFetching && !isError ? (
        <>
          {data.requestable &&
            data.requestable.internal_status === 'Rerecording VO' && (
              <List>
                <ListItem>
                  <Typography variant='h6' sx={{width: '50%'}}>
                    Task Description
                  </Typography>
                  <Typography variant='subtitle1'>
                    {data.requestable.message?.rejected_note}
                  </Typography>
                </ListItem>
              </List>
            )}
          <TableContainer className='p-2 px-4 mt-1 blue-variant15'>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography fontWeight={600}>File name</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>Message</Typography>
                  </TableCell>
                  {isWalkup || isTeamRoster ? (
                    <TableCell>
                      <Typography fontWeight={600}>Client File</Typography>
                    </TableCell>
                  ) : null}
                  <TableCell>
                    <Typography fontWeight={600} width={150}>
                      AI Voice
                    </Typography>
                  </TableCell>
                  <TableCell align='center'>
                    <Typography fontWeight={600}>Upload</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {content.map((row: IMsgAudioUpload) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      '&:last-child td, &:last-child th': {border: 0},
                    }}>
                    <TableCell id={`file-name-${row.id}`}>
                      <Typography>
                        <TrackNameCell value={row.prepopulated_file_name} />
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {(row?.name || row?.requestable?.name) && (
                        <Typography variant='caption'>
                          <b>Name: </b>
                          {row?.name || row?.requestable?.name}
                        </Typography>
                      )}
                      <Stack
                        direction={'row'}
                        alignItems={'center'}
                        spacing={1}>
                        {row.additional_details ? (
                          <AdditionaInformationButton
                            playerDetails={row.additional_details}
                          />
                        ) : null}
                        <Typography>
                          <pre>{row.message}</pre>
                        </Typography>
                      </Stack>
                    </TableCell>
                    {isWalkup || isTeamRoster ? (
                      <TableCell>
                        {row.initial_url ? (
                          <Stack direction='row'>
                            <MusicPreviewBtn
                              id={row.id}
                              url={row.initial_url}
                              track_name={row.prepopulated_file_name}
                              track_artist={''}
                              isDownloadable
                              downloadFileName={row.prepopulated_file_name}
                            />
                          </Stack>
                        ) : (
                          'NA'
                        )}
                      </TableCell>
                    ) : null}
                    <TableCell>
                      {isNR &&
                      data?.task_slug === 'base-recording-pending' &&
                      row.voice_artist ? (
                        <Typography className='flex items-center flex gap-2'>
                          {(() => {
                            const artist = voiceArtistData?.find(
                              va => va.id === row.voice_artist,
                            );
                            return (
                              <>
                                <MusicPreviewBtn
                                  id={artist?.id!}
                                  url={artist?.sample_url!}
                                  track_name={artist?.name!}
                                  track_artist={artist?.name!}
                                />
                                <span>{artist?.name}</span>
                              </>
                            );
                          })()}
                        </Typography>
                      ) : (
                        <FormControl fullWidth>
                          <InputLabel id='voice-artist-label'>
                            Voice Artist
                          </InputLabel>
                          <Select
                            id='voice-artist-select'
                            label='Voice Artist'
                            onChange={(e: SelectChangeEvent<string>) => {
                              handleChangeVoiceArtist(e, row.id);
                            }}>
                            {voiceArtistData?.map(value => (
                              <MenuItem key={value.id} value={value.id}>
                                {value.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </TableCell>
                    <TableCell align='center'>
                      {row.matched ? (
                        <CheckCircleIcon color='success' />
                      ) : (
                        <CancelIcon color='error' />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : isFetching ? (
        <>
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </>
      ) : null}
      {filesSelected ? (
        <Box className={classes.r_end} mt={2}>
          <input
            accept='audio/*'
            multiple
            type='file'
            id='select-voice'
            style={{display: 'none'}}
            onChange={handleFileChange}
          />
          <label htmlFor='select-voice'>
            <Button variant='contained' color='primary' component='span'>
              Upload Files
            </Button>
          </label>
        </Box>
      ) : null}
      {filesSelected === 0 && isFetched ? (
        <Box className={classes.r_end} mt={2}>
          <Button
            variant='contained'
            onClick={handleSubmit}
            disabled={isPreview || voices.length !== content.length}>
            Submit
          </Button>
        </Box>
      ) : null}

      {apiState.status !== '' && (
        <DialogApiRequestStatus
          apiState={apiState}
          onRetry={handleSubmit}
          onEdit={handleEdit}
          onClose={handleClose}
        />
      )}
    </Box>
  );
};
export default OnboardRecording;

export const SponsorMessageDetailsCmpt = ({
  className = 'mb-2',
  message,
}: {
  className?: string;
  message: IMessage1 | MessagesProps;
}) => {
  const messageTitle = message?.name || message?.requestable?.name;
  return (
    <Box className={className}>
      {messageTitle ? (
        <Typography variant='caption' className='mb-1 block'>
          <b>Name: </b>
          {messageTitle}
        </Typography>
      ) : null}
      {message.original_message && message.original_message?.length > 0 ? (
        <MessageDetailBox
          label='Original Message'
          value={message.original_message}
        />
      ) : null}
      {message.rejected_note && message.rejected_note?.length > 0 ? (
        <MessageDetailBox
          label='Notes from Sponsor'
          value={message.rejected_note}
        />
      ) : null}
      {message.copy_writer_content &&
      message.copy_writer_content?.length > 0 ? (
        <MessageDetailBox
          label='Copywriter Content'
          value={message.copy_writer_content}
        />
      ) : null}
    </Box>
  );
};

export const MessageDetailBox = ({
  label,
  value,
  className = '',
}: {
  label: string;
  value: string;
  className?: string;
}) => {
  return (
    <Box className={className}>
      <Typography variant='h6'>{label}</Typography>
      <TextField fullWidth multiline rows={2} value={value} disabled />
    </Box>
  );
};

export const AdditionaInformationButton = ({
  playerDetails,
}: {
  playerDetails: IAdditionalPlayerInformationType;
}) => {
  const DetailsComponent = (
    <>
      <Typography>
        <b>Pre Announcement :</b>{' '}
        {playerDetails.pre_announcement ? playerDetails.pre_announcement : 'NA'}
      </Typography>
      <Typography>
        <b>Player No :</b> {playerDetails.player_no}
      </Typography>
      <Typography>
        <b>First Name :</b> {playerDetails.first_name}
      </Typography>
      <Typography>
        <b>Last Name :</b> {playerDetails.last_name}
      </Typography>
      {playerDetails.manual_message ? (
        <Typography>
          <b>Manual Message :</b> {playerDetails.manual_message}
        </Typography>
      ) : null}
      {playerDetails.manual_file_url ? (
        <Typography>
          <b>Manual file :</b>{' '}
          <MusicPreviewBtn
            id={`${playerDetails.player_no}_${playerDetails.first_name}_${playerDetails.last_name}`}
            url={playerDetails.manual_file_url!}
            track_name={`${playerDetails.player_no}_${playerDetails.first_name}_${playerDetails.last_name}`}
            track_artist={`${playerDetails.player_no}_${playerDetails.first_name}_${playerDetails.last_name}`}
          />
        </Typography>
      ) : null}
    </>
  );
  return (
    <>
      <Tooltip title={DetailsComponent} placement='top'>
        <InfoIcon />
      </Tooltip>
    </>
  );
};
